import React from 'react';
import { lazy, Suspense, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Layout } from '../components/shared/Layout';
import { useAuth } from '../hooks/useAuth';
import { Loader } from '../components/ui/Loader';
import { FeeDuePrompt } from '../components/shared/fees/FeeDuePrompt';
const AdminDashboard = /*#__PURE__*/ lazy(() => import('../components/admin/AdminDashboard'));
const ContentCreatorDashboard = /*#__PURE__*/ lazy(
  () => import('../components/creator/ContentCreatorDashboard')
);
const StudentDashboard = /*#__PURE__*/ lazy(() =>
  import('@/features/student').then((m) => ({ default: m.StudentDashboard }))
);
const ParentDashboard = /*#__PURE__*/ lazy(() => import('../components/parent/ParentDashboard'));
const ROLES = ['super-admin', 'content-creator', 'student', 'parent'];
const DashboardFallback = () => <Loader label="Loading dashboard…" fullHeight />;
function DashboardByRole() {
  const { role } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const validRole = role && ROLES.includes(role) ? role : null;
  useEffect(() => {
    if (!validRole && user) {
      navigate(`/dashboard/${user.role}`, {
        replace: true,
      });
    }
  }, [validRole, user, navigate]);
  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!validRole) {
    return (
      <Layout view={null} user={user} onLogout={handleLogout}>
        <DashboardFallback />
      </Layout>
    );
  }
  const renderDashboard = () => {
    switch (validRole) {
      case 'super-admin':
        return <AdminDashboard />;
      case 'content-creator':
        return <ContentCreatorDashboard />;
      case 'student':
        return <StudentDashboard />;
      case 'parent':
        return <ParentDashboard />;
      default:
        return (
          <div className="text-center py-20">
            <p className="text-slate-500">Please select a role to continue.</p>
          </div>
        );
    }
  };
  return (
    <Layout view={validRole} user={user} onLogout={handleLogout}>
      <Suspense fallback={<DashboardFallback />}>{renderDashboard()}</Suspense>
      {validRole === 'student' || validRole === 'parent' ? <FeeDuePrompt /> : null}
    </Layout>
  );
}
export default DashboardByRole;
